import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as subscriptionService from '../services/subscriptionService';
import type { CompanySubscriptionDetail, SubscriptionKPIs, UpdateSubscriptionInput } from '../services/subscriptionService';
import * as pricingPlanService from '../services/pricingPlanService';
import type { PricingPlan } from '../services/pricingPlanService';

function CompanySubscriptionManagement(): React.ReactElement {
  const { token } = useAuth();

  // Data state
  const [companies, setCompanies] = useState<CompanySubscriptionDetail[]>([]);
  const [kpis, setKpis] = useState<SubscriptionKPIs | null>(null);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  // Edit modal
  const [editingCompany, setEditingCompany] = useState<CompanySubscriptionDetail | null>(null);
  const [editForm, setEditForm] = useState<UpdateSubscriptionInput>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load plans once
  useEffect(() => {
    async function loadPlans() {
      if (!token) return;
      try {
        const allPlans = await pricingPlanService.getAllPlans(token);
        setPlans(allPlans.filter(p => p.is_active));
      } catch (err) {
        console.error('Error loading plans:', err);
      }
    }
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load companies and KPIs
  const loadData = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [subsData, kpiData] = await Promise.all([
        subscriptionService.getCompanySubscriptions({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined,
          subscription_status: statusFilter || undefined,
          subscription_tier: tierFilter || undefined
        }, token),
        subscriptionService.getSubscriptionKPIs(token)
      ]);
      setCompanies(subsData.companies);
      setTotalPages(subsData.pagination.totalPages);
      setTotal(subsData.pagination.total);
      setKpis(kpiData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentPage, statusFilter, tierFilter]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      loadData();
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openEditModal = (company: CompanySubscriptionDetail) => {
    setEditingCompany(company);
    setEditForm({
      subscription_tier: company.subscription_tier || 'free',
      subscription_status: company.subscription_status || 'trial',
      trial_ends_at: company.trial_ends_at ? company.trial_ends_at.split('T')[0] : '',
      subscription_ends_at: company.subscription_ends_at ? company.subscription_ends_at.split('T')[0] : '',
      max_users: company.max_users,
      max_projects: company.max_projects,
      bug_reporting_enabled: company.bug_reporting_enabled
    });
    setSaveError(null);
  };

  const handlePlanChange = (newTier: string) => {
    const plan = plans.find(p => p.internal_key === newTier);
    setEditForm(prev => ({
      ...prev,
      subscription_tier: newTier,
      max_users: plan ? plan.max_users : prev.max_users,
      max_projects: plan ? plan.max_projects : prev.max_projects
    }));
  };

  const handleSave = async () => {
    if (!editingCompany || !token) return;
    try {
      setSaving(true);
      setSaveError(null);
      await subscriptionService.updateCompanySubscription(editingCompany.id, editForm, token);
      setEditingCompany(null);
      loadData();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
      case 'trial':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Trial</span>;
      case 'suspended':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Suspended</span>;
      case 'cancelled':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Cancelled</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Unknown</span>;
    }
  };

  const formatLimit = (count: number, limit: number | null) => {
    const limitStr = limit ? String(limit) : '\u221e';
    const isAtLimit = limit !== null && count >= limit;
    return (
      <span className={isAtLimit ? 'text-red-600 font-semibold' : ''}>
        {count} / {limitStr}
      </span>
    );
  };

  const formatDaysOnPlan = (days: number) => {
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.floor(days / 30)}mo`;
    return `${Math.floor(days / 365)}y ${Math.floor((days % 365) / 30)}mo`;
  };

  const formatDateInfo = (company: CompanySubscriptionDetail) => {
    if (company.subscription_status === 'trial') {
      if (company.days_remaining_trial !== null && company.days_remaining_trial >= 0) {
        const isExpiringSoon = company.days_remaining_trial <= 7;
        return (
          <span className={isExpiringSoon ? 'text-amber-600 font-medium' : 'text-gray-600'}>
            {company.days_remaining_trial}d left on trial
          </span>
        );
      }
      return <span className="text-red-600 font-medium">Trial expired</span>;
    }
    if (company.subscription_status === 'active') {
      if (company.days_remaining_subscription !== null) {
        return <span className="text-gray-600">{company.days_remaining_subscription}d remaining</span>;
      }
      return <span className="text-gray-500">No end date</span>;
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <Layout>
      <div className="p-6 max-w-full">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">Company Subscriptions</h1>
          <p className="text-sm text-text-secondary mt-1">Manage subscription plans, limits, and billing status for all companies</p>
        </div>

        {/* KPI Cards */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-indigo-500">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</p>
              <p className="text-2xl font-bold text-indigo-600">{kpis.total_companies}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Active</p>
              <p className="text-2xl font-bold text-green-600">{kpis.active_subscriptions}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-blue-500">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">On Trial</p>
              <p className="text-2xl font-bold text-blue-600">{kpis.trial_companies}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-amber-500">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expiring 7d</p>
              <p className="text-2xl font-bold text-amber-600">{kpis.expiring_trials_7days}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-red-500">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Suspended</p>
              <p className="text-2xl font-bold text-red-600">{kpis.suspended_companies}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-gray-400">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cancelled</p>
              <p className="text-2xl font-bold text-gray-600">{kpis.cancelled_companies}</p>
            </div>
          </div>
        )}

        {/* Filters Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-text-primary"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspended</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary"
          >
            <option value="">All Plans</option>
            {plans.map(p => (
              <option key={p.internal_key} value={p.internal_key}>{p.name}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500">{total} companies</span>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading subscriptions...</span>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p>No companies found matching your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Projects</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bug Reporting</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Member Since</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text-primary">{company.name}</div>
                        {company.industry && (
                          <div className="text-xs text-gray-500">{company.industry}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-primary">{company.plan_name || company.subscription_tier || 'None'}</div>
                        {company.plan_price_monthly !== null && (
                          <div className="text-xs text-gray-500">${company.plan_price_monthly}/mo</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(company.subscription_status)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatLimit(company.user_count, company.max_users)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatLimit(company.project_count, company.max_projects)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            if (!token) return;
                            try {
                              await subscriptionService.updateCompanySubscription(
                                company.id,
                                { bug_reporting_enabled: !company.bug_reporting_enabled },
                                token
                              );
                              loadData();
                            } catch (err) {
                              console.error('Failed to toggle bug reporting:', err);
                            }
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            company.bug_reporting_enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          title={company.bug_reporting_enabled ? 'Disable bug reporting' : 'Enable bug reporting'}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              company.bug_reporting_enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                            }`}
                            style={{ transform: company.bug_reporting_enabled ? 'translateX(18px)' : 'translateX(2px)' }}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatDateInfo(company)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDaysOnPlan(company.days_on_plan)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openEditModal(company)}
                          className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded-full hover:bg-indigo-50 transition-colors"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  const page = startPage + i;
                  if (page > totalPages) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === currentPage
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingCompany && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setEditingCompany(null)} />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 z-10">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary">Manage Subscription</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{editingCompany.name}</p>
                  </div>
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Read-only info */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Current Users:</span>{' '}
                    <span className="font-medium text-text-primary">{editingCompany.user_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current Projects:</span>{' '}
                    <span className="font-medium text-text-primary">{editingCompany.project_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Industry:</span>{' '}
                    <span className="font-medium text-text-primary">{editingCompany.industry || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Member Since:</span>{' '}
                    <span className="font-medium text-text-primary">{formatDaysOnPlan(editingCompany.days_on_plan)} ago</span>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  {/* Plan */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Plan</label>
                    <select
                      value={editForm.subscription_tier || ''}
                      onChange={(e) => handlePlanChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                    >
                      {plans.map(p => (
                        <option key={p.internal_key} value={p.internal_key}>
                          {p.name} {p.price_monthly ? `($${p.price_monthly}/mo)` : '(Free)'}
                        </option>
                      ))}
                    </select>
                    {/* Warning if current usage exceeds plan limits */}
                    {(() => {
                      const selectedPlan = plans.find(p => p.internal_key === editForm.subscription_tier);
                      if (!selectedPlan) return null;
                      const warnings = [];
                      if (selectedPlan.max_users && editingCompany.user_count > selectedPlan.max_users) {
                        warnings.push(`Current users (${editingCompany.user_count}) exceeds plan limit (${selectedPlan.max_users})`);
                      }
                      if (selectedPlan.max_projects && editingCompany.project_count > selectedPlan.max_projects) {
                        warnings.push(`Current projects (${editingCompany.project_count}) exceeds plan limit (${selectedPlan.max_projects})`);
                      }
                      if (warnings.length === 0) return null;
                      return (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                          {warnings.map((w, i) => <div key={i}>{w}</div>)}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Status</label>
                    <select
                      value={editForm.subscription_status || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, subscription_status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="trial">Trial</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Trial End Date */}
                  {editForm.subscription_status === 'trial' && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Trial End Date</label>
                      <input
                        type="date"
                        value={editForm.trial_ends_at || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, trial_ends_at: e.target.value || null }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {/* Subscription End Date */}
                  {editForm.subscription_status === 'active' && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Subscription End Date</label>
                      <input
                        type="date"
                        value={editForm.subscription_ends_at || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, subscription_ends_at: e.target.value || null }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {/* Max Users & Max Projects */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Max Users <span className="text-gray-400 font-normal">(empty = unlimited)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_users ?? ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, max_users: e.target.value === '' ? null : Number(e.target.value) }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Currently using {editingCompany.user_count}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Max Projects <span className="text-gray-400 font-normal">(empty = unlimited)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_projects ?? ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, max_projects: e.target.value === '' ? null : Number(e.target.value) }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-text-primary focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Currently using {editingCompany.project_count}</p>
                    </div>
                  </div>

                  {/* Bug Reporting Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-text-primary">Bug Reporting</label>
                      <p className="text-xs text-gray-400 mt-0.5">Allow users to report bugs to the Nivoon team</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm(prev => ({ ...prev, bug_reporting_enabled: !prev.bug_reporting_enabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editForm.bug_reporting_enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        style={{ transform: editForm.bug_reporting_enabled ? 'translateX(22px)' : 'translateX(3px)' }}
                      />
                    </button>
                  </div>
                </div>

                {/* Save Error */}
                {saveError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {saveError}
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CompanySubscriptionManagement;
